import type { Tone } from '@/design-system';

import type {
    AuditLogResource,
    CreditAdjustmentResource,
    ImpersonationSessionResource,
    ImpersonationTicketResource,
    PlanResource,
    PlatformMetricResource,
    PlatformTemplateResource,
    PlatformTenantResource,
    PricingResource,
    ProvisionedTenantResource,
    TemplateRolloutResource,
} from './types';

/**
 * Platformbeheer: de keuringsbedrijven van bovenaf gezien.
 *
 * Dit hoort bij de context `Platform` in de API, maar leeft in `entities/tenant`
 * omdat het over dezelfde entiteit gaat - één datamodel, een andere uitsnede
 * (principe 8). Alles hieronder is alleen bereikbaar voor `platform_admin`, die
 * alle platformrechten heeft. De schermen kennen alleen deze vorm; de
 * `to*`-functies zijn de enige plek waar de lijnvorm (`types.ts`) wordt vertaald.
 */

/* -------------------------------------------------------------------------- */
/* Organisaties                                                                */
/* -------------------------------------------------------------------------- */

export const TENANT_STATUSES = ['trial', 'active', 'suspended', 'cancelled'] as const;

export type TenantStatus = (typeof TENANT_STATUSES)[number];

/** Gelijk aan `TenantStatus::label()` in de API. */
export const TENANT_STATUS_LABELS: Record<TenantStatus, string> = {
    trial: 'Proefperiode',
    active: 'Actief',
    suspended: 'Opgeschort',
    cancelled: 'Beëindigd',
};

/** Amber voor wat aandacht vraagt, nooit rood: een opgeschort bedrijf is geen fout. */
export const TENANT_STATUS_TONES: Record<TenantStatus, Tone> = {
    trial: 'info',
    active: 'accent',
    suspended: 'warn',
    cancelled: 'neutral',
};

function tenantStatusOf(value: string): TenantStatus {
    return (TENANT_STATUSES as readonly string[]).includes(value)
        ? (value as TenantStatus)
        : 'active';
}

export interface PlatformTenant {
    uuid: string;
    name: string;
    slug: string;
    status: TenantStatus;
    planCode: string | null;
    planName: string | null;
    email: string | null;
    phone: string | null;
    timezone: string | null;
    trialEndsAt: string | null;
    suspendedAt: string | null;
    suspendedReason: string | null;
    createdAt: string | null;
    /** De tegoedregels, die alleen het platform zet. */
    creditRules: CreditRules;
}

/**
 * Of nieuwe keuringen stoppen als de keuringen op zijn, en hoe ver het saldo onder
 * nul mag. Alleen platformbeheer wijzigt dit: met blokkeren uit of een marge maakt
 * een bedrijf keuringen die het niet betaalde.
 */
export interface CreditRules {
    blockOnEmpty: boolean;
    graceCredits: number;
}

export function toPlatformTenant(resource: PlatformTenantResource): PlatformTenant {
    return {
        uuid: resource.uuid,
        name: resource.name,
        slug: resource.slug,
        status: tenantStatusOf(resource.status),
        planCode: resource.plan_code,
        planName: resource.plan_name,
        email: resource.email,
        phone: resource.phone,
        timezone: resource.timezone,
        trialEndsAt: resource.trial_ends_at,
        suspendedAt: resource.suspended_at,
        suspendedReason: resource.suspended_reason,
        createdAt: resource.created_at,
        creditRules: {
            blockOnEmpty: resource.block_on_empty,
            graceCredits: resource.grace_credits,
        },
    };
}

/** Een abonnement van de prijzenlijst: per maand, exclusief btw, met de keuringen die erbij horen. */
export interface Plan {
    code: string;
    name: string;
    description: string | null;
    monthlyPriceCents: number;
    includedInspections: number;
    featured: boolean;
}

export function toPlans(resources: readonly PlanResource[]): Plan[] {
    return [...resources]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((resource) => ({
            code: resource.code,
            name: resource.name,
            description: resource.description,
            monthlyPriceCents: resource.monthly_price_cents,
            includedInspections: resource.included_inspections,
            featured: resource.featured,
        }));
}

/**
 * De prijzenlijst van Smartkeur, zoals de website hem toont: de abonnementen, de
 * proef, een extra keuring en een losse rapportage. Bedragen in hele centen,
 * exclusief btw.
 */
export interface PriceList {
    vatRate: number;
    trialInspections: number;
    extraInspectionPriceCents: number;
    singleReport: { name: string; priceCents: number };
    plans: Plan[];
}

export function toPriceList(resource: PricingResource): PriceList {
    return {
        vatRate: resource.vat_rate,
        trialInspections: resource.trial_inspections,
        extraInspectionPriceCents: resource.extra_inspection_price_cents,
        singleReport: {
            name: resource.single_report.name,
            priceCents: resource.single_report.price_cents,
        },
        plans: toPlans(resource.plans.map((plan) => ({ ...plan, is_public: true }))),
    };
}

/** Opschorten en tegoed boeken eisen een reden van minimaal tien tekens. */
export const PLATFORM_REASON_MIN_LENGTH = 10;

/* -------------------------------------------------------------------------- */
/* Handmatige tegoedboeking                                                    */
/* -------------------------------------------------------------------------- */

export const MANUAL_BOOKING_DIRECTIONS = ['credit', 'debit'] as const;

export type ManualBookingDirection = (typeof MANUAL_BOOKING_DIRECTIONS)[number];

export const MANUAL_BOOKING_LABELS: Record<ManualBookingDirection, string> = {
    credit: 'Bijboeken',
    debit: 'Afboeken',
};

/** Credits zijn hele eenheden; afboeken is een negatieve boeking. */
export function signedCredits(direction: ManualBookingDirection, credits: number): number {
    return direction === 'debit' ? -Math.abs(credits) : Math.abs(credits);
}

export interface ManualBookingResult {
    transactionUuid: string;
    credits: number;
    balanceAfter: number;
    /** Een herhaalde klik bereikte een bestaande boeking; er is niets extra geboekt. */
    replayed: boolean;
}

export function toManualBookingResult(resource: CreditAdjustmentResource): ManualBookingResult {
    return {
        transactionUuid: resource.transaction_uuid,
        credits: resource.amount,
        balanceAfter: resource.balance_after,
        replayed: resource.was_replayed,
    };
}

/* -------------------------------------------------------------------------- */
/* Impersonatie                                                                */
/* -------------------------------------------------------------------------- */

export const IMPERSONATION_MAX_MINUTES = 60;

export interface ImpersonationSessionRecord {
    uuid: string;
    reason: string;
    startedAt: string;
    expiresAt: string;
    endedAt: string | null;
    active: boolean;
    platformUserName: string | null;
    targetUserUuid: string | null;
    targetUserName: string | null;
}

export function toImpersonationSessionRecord(
    resource: ImpersonationSessionResource,
): ImpersonationSessionRecord {
    return {
        uuid: resource.uuid,
        reason: resource.reason,
        startedAt: resource.started_at,
        expiresAt: resource.expires_at,
        endedAt: resource.ended_at,
        active: resource.is_active,
        platformUserName: resource.platform_user?.name ?? null,
        targetUserUuid: resource.target_user?.uuid ?? null,
        targetUserName: resource.target_user?.name ?? null,
    };
}

/**
 * Een lopende "meekijken als"-sessie, zoals de balk bovenaan hem toont. Het
 * tijdelijke token komt precies één keer langs; de app bewaart het alleen in
 * dit tabblad.
 */
export interface ImpersonationSession {
    sessionUuid: string;
    token: string;
    expiresAt: string;
    tenantUuid: string;
    tenantName: string;
    userUuid: string;
    userName: string;
    reason: string;
    startedAt: string;
}

export function toImpersonationSession(
    ticket: ImpersonationTicketResource,
    context: { tenantUuid: string; tenantName: string; userUuid: string; userName: string },
): ImpersonationSession {
    return {
        sessionUuid: ticket.session.uuid,
        token: ticket.token,
        expiresAt: ticket.expires_at,
        tenantUuid: context.tenantUuid,
        tenantName: context.tenantName,
        userUuid: ticket.session.target_user?.uuid ?? context.userUuid,
        userName: ticket.session.target_user?.name ?? context.userName,
        reason: ticket.session.reason,
        startedAt: ticket.session.started_at,
    };
}

/* -------------------------------------------------------------------------- */
/* Metrics                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * De nachtelijke momentopname, opgeteld over de gekozen dagen en bedrijven.
 * Ontbreekt een getal op één dag, dan is het totaal onbekend (`null`) - een
 * dashboard hoort dan "onbekend" te tonen en niet een te laag getal.
 */
export interface PlatformMetrics {
    days: number;
    ordersCreated: number | null;
    inspectionsCompleted: number | null;
    reportsSent: number | null;
    /** Doorlooptijd van rapport in minuten, gewogen naar het aantal verstuurde rapporten. */
    averageLeadTimeMinutes: number | null;
    creditsConsumed: number | null;
    /** Het hoogste aantal actieve gebruikers op één dag. */
    activeUsers: number | null;
    complete: boolean;
}

function weightedLeadTime(resources: readonly PlatformMetricResource[]): number | null {
    let minutes = 0;
    let reports = 0;

    for (const row of resources) {
        if (row.reports_sent === null) {
            return null;
        }

        if (row.reports_sent === 0) {
            continue;
        }

        if (row.average_lead_time_minutes === null) {
            return null;
        }

        minutes += row.average_lead_time_minutes * row.reports_sent;
        reports += row.reports_sent;
    }

    return reports === 0 ? null : Math.round(minutes / reports);
}

export function toPlatformMetrics(resources: readonly PlatformMetricResource[]): PlatformMetrics {
    const sum = (pick: (row: PlatformMetricResource) => number | null): number | null =>
        resources.length === 0
            ? null
            : resources.reduce<number | null>((total, row) => {
                  const value = pick(row);

                  return total === null || value === null ? null : total + value;
              }, 0);

    const perDay = new Map<string, number | null>();

    for (const row of resources) {
        const current = perDay.has(row.date) ? (perDay.get(row.date) ?? null) : 0;

        perDay.set(
            row.date,
            current === null || row.active_users === null ? null : current + row.active_users,
        );
    }

    const dayTotals = [...perDay.values()];

    return {
        days: perDay.size,
        ordersCreated: sum((row) => row.orders_created),
        inspectionsCompleted: sum((row) => row.inspections_completed),
        reportsSent: sum((row) => row.reports_sent),
        averageLeadTimeMinutes: weightedLeadTime(resources),
        creditsConsumed: sum((row) => row.credits_consumed),
        activeUsers:
            dayTotals.length === 0 || dayTotals.includes(null)
                ? null
                : Math.max(...(dayTotals as number[])),
        complete: resources.length > 0 && resources.every((row) => row.is_complete),
    };
}

export interface PlatformMetricFilters {
    tenantUuid?: string;
    from?: string;
    until?: string;
}

/* -------------------------------------------------------------------------- */
/* Audit log                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * De `log_name`s die de API schrijft (`PlatformAuditEvent::logName()` en het
 * grootboek van Billing), met hun Nederlandse naam.
 */
export const AUDIT_LOG_NAMES = [
    'tenant',
    'credits',
    'impersonation',
    'billing',
    'feature_flag',
    'template',
    'notifications',
    'orders',
    'identity',
    'services',
] as const;

export type AuditLogName = (typeof AUDIT_LOG_NAMES)[number];

export const AUDIT_LOG_NAME_LABELS: Record<AuditLogName, string> = {
    tenant: 'Bedrijf',
    credits: 'Tegoed',
    impersonation: 'Meekijken',
    billing: 'Facturatie',
    feature_flag: 'Functies',
    template: 'Sjablonen',
    notifications: 'Berichten',
    orders: 'Keuringen',
    identity: 'Gebruikers',
    services: 'Diensten',
};

export function auditLogNameLabel(logName: string | null): string {
    return (AUDIT_LOG_NAMES as readonly string[]).includes(logName ?? '')
        ? AUDIT_LOG_NAME_LABELS[logName as AuditLogName]
        : 'Overig';
}

export interface AuditLogEntry {
    /** Kan ontbreken zolang de migratie die uuid's toevoegt nog niet draaide. */
    uuid: string | null;
    tenantUuid: string | null;
    logName: string | null;
    event: string | null;
    description: string;
    /** De reden uit de eigenschappen, als de handeling er een had. */
    reason: string | null;
    duringImpersonation: boolean;
    actorName: string | null;
    createdAt: string | null;
}

export function toAuditLogEntry(resource: AuditLogResource): AuditLogEntry {
    const properties = Array.isArray(resource.properties) ? {} : resource.properties;
    const reason = properties.reason;

    return {
        uuid: resource.uuid,
        tenantUuid: resource.tenant_uuid,
        logName: resource.log_name,
        event: resource.event,
        description: resource.description,
        reason: typeof reason === 'string' && reason !== '' ? reason : null,
        duringImpersonation: resource.during_impersonation,
        actorName: resource.actor?.name ?? null,
        createdAt: resource.created_at,
    };
}

export interface AuditLogFilters {
    tenantUuid?: string;
    logName?: string;
    from?: string;
    until?: string;
    impersonatedOnly?: boolean;
    page?: number;
}

/* -------------------------------------------------------------------------- */
/* Een keuringsbedrijf aanmaken                                                */
/* -------------------------------------------------------------------------- */

/** De vorm van een slug in de API: kleine letters, cijfers en losse koppeltekens. */
export const TENANT_SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** "Zuidkeur Voorbeeld-West B.V." → `zuidkeur-voorbeeld-west-b-v`. */
export function slugFor(name: string): string {
    return name
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 64)
        .replace(/-+$/g, '');
}

export interface NewTenantInput {
    name: string;
    slug: string;
    email: string | null;
    phone: string | null;
    owner: { name: string; email: string; phone: string | null };
    /** Hele credits, als eerste boeking in het grootboek. */
    startingCredits: number;
    /** De vragenlijsten van het platform die het bedrijf meteen krijgt. */
    templateUuids: string[];
}

export interface ProvisionedTenant {
    tenant: PlatformTenant;
    owner: {
        /** `null` zolang de eigenaar zijn uitnodiging nog niet heeft geaccepteerd. */
        uuid: string | null;
        name: string;
        email: string;
        /** Had al een Smartkeur-account: alleen een lidmaatschap erbij, geen mail. */
        existingAccount: boolean;
        invitationSent: boolean;
    };
    startingCredits: number;
    templates: Array<{ uuid: string; name: string }>;
}

export function toProvisionedTenant(resource: ProvisionedTenantResource): ProvisionedTenant {
    return {
        tenant: toPlatformTenant(resource.tenant),
        owner: {
            uuid: resource.owner.uuid === '' ? null : resource.owner.uuid,
            name: resource.owner.name,
            email: resource.owner.email,
            existingAccount: resource.owner.was_existing_user,
            invitationSent: resource.owner.invitation_sent,
        },
        startingCredits: resource.starting_credits,
        templates: resource.templates,
    };
}

/* -------------------------------------------------------------------------- */
/* Vragenlijsten uitrollen                                                     */
/* -------------------------------------------------------------------------- */

/** Zoveel bedrijven neemt één uitrol hoogstens (`RollOutTemplateRequest::MAXIMUM_TENANTS`). */
export const ROLLOUT_MAX_TENANTS = 50;

export interface PlatformTemplate {
    uuid: string;
    name: string;
    /** `nta8060` → `NTA 8060`. */
    norm: string;
    versionNumber: number | null;
    publishedAt: string | null;
}

/** `nta8060` → `NTA 8060`, `nen2767` → `NEN 2767`; iets anders blijft zoals het is. */
export function normLabel(inspectionType: string): string {
    const match = /^([a-z]+)(\d+)$/i.exec(inspectionType);

    return match ? `${match[1]?.toUpperCase()} ${match[2]}` : inspectionType;
}

export function toPlatformTemplate(resource: PlatformTemplateResource): PlatformTemplate {
    return {
        uuid: resource.uuid,
        name: resource.name,
        norm: normLabel(resource.inspection_type),
        versionNumber: resource.version_number,
        publishedAt: resource.published_at,
    };
}

export const ROLLOUT_STATUSES = ['pending', 'completed', 'updated', 'skipped', 'failed'] as const;

export type RolloutStatus = (typeof ROLLOUT_STATUSES)[number];

/**
 * Uitgerold en bijgewerkt zijn groen (er is iets veranderd), overgeslagen
 * neutraal (was al bij), mislukt amber - nooit rood.
 */
export const ROLLOUT_STATUS_TONES: Record<RolloutStatus, Tone> = {
    pending: 'info',
    completed: 'accent',
    updated: 'accent',
    skipped: 'neutral',
    failed: 'warn',
};

export interface TemplateRollout {
    tenantUuid: string;
    status: RolloutStatus;
    /** Het label van de API: "Uitgerold", "Bijgewerkt", "Overgeslagen", "Mislukt". */
    statusLabel: string;
    /** De reden bij mislukt, of waarom een bedrijf met rust is gelaten. */
    error: string | null;
}

/**
 * De API geeft de uitkomsten in de volgorde van `tenant_uuids`, zonder de
 * tenant erbij; de index koppelt ze weer aan het bedrijf.
 */
export function toTemplateRollouts(
    resources: readonly TemplateRolloutResource[],
    tenantUuids: readonly string[],
): TemplateRollout[] {
    return resources.map((resource, index) => ({
        tenantUuid: tenantUuids[index] ?? '',
        status: (ROLLOUT_STATUSES as readonly string[]).includes(resource.status)
            ? (resource.status as RolloutStatus)
            : 'failed',
        statusLabel: resource.status_label,
        error: resource.error,
    }));
}
